import styles from "../style.module.scss";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { App, Select } from "antd";
import ShareIcon from "assets/icons/ShareIcon";
import VLButton from "views/uikit/Buttons/VLButton";
import SelectList from "views/uikit/FormFields/SelectList";
import { useDatasetShare } from "../useDatasetShare";
import TextWithTooltip from "views/components/TextWithTooltip";
import ChevronIcon from "assets/icons/ChevronIcon";
import { BaseOptionType } from "antd/es/select";

export const DatasetShare = ({
  datasetId,
  onClose,
}: {
  onClose: () => void;
  datasetId: string;
}) => {
  const {
    selectedRole,
    setSelectedRole,
    selectOpen,
    setSelectOpen,
    selectedUser,
    setSelectedUser,
    options,
    collaborators,
    handleAddUsers,
    handleRemoveUser,
    handleUpdateUserRole,
    rolesList,
    owners,
    isSubmitting,
    isRevoking,
    isUpdating,
    canManageAccess,
    datasetName,
  } = useDatasetShare(datasetId);

  const { modal } = App.useApp();

  const openDeleteModal = (userId: string) => {
    modal.confirm({
      title: "Remove Access",
      content: `Are you sure you want to remove this user’s access to Dataset ${datasetName}?`,
      width: 520,
      okText: "Remove",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      closable: true,
      onOk: () => handleRemoveUser(userId),
    });
  };

  const openUpdateRoleModal = (
    userId: string,
    currentRole: string,
    newRole: string,
  ) => {
    modal.confirm({
      title: "Update Role",
      content: `Are you sure you want to change the role from ${currentRole} to ${newRole}?`,
      width: 520,
      okText: "Update",
      cancelText: "Cancel",
      closable: true,
      onOk: () => handleUpdateUserRole(userId, newRole),
    });
  };

  const handleRoleChange = (
    userId: string,
    currentRole: string,
    value: string,
  ) => {
    if (value === "remove") {
      openDeleteModal(userId);
    } else {
      openUpdateRoleModal(userId, currentRole, value);
    }
  };

  const rolesListWithRemove = [
    ...rolesList,
    { label: "Remove", value: "remove" },
  ];

  const getInitials = (firstName: string) => {
    const firstInitial = firstName?.charAt(0) || "";
    return `${firstInitial}`.toUpperCase() || "?";
  };

  const getColorIndex = (id: string) => {
    return id.charCodeAt(0) % 5;
  };

  const handleSeletedUser = (userId: string | string[]) => {
    const userIdValue = Array.isArray(userId) ? userId[0] : userId;
    const foundUser = options.find((o) => o.value === userIdValue);
    setSelectedUser(foundUser || null);
  };

  const renderSingleUser = (singleUser: BaseOptionType) => {
    if (!singleUser) return null;
    const { username = "", label } = singleUser;
    return (
      <div className={styles.userListItem}>
        <div
          className={styles.userAvatar}
          data-color-index={label ? getColorIndex(label) : "default"}
        >
          {getInitials(label)}
        </div>
        <div className={styles.userInfo}>
          <div className={styles.userName}>
            <TextWithTooltip className={styles.name} text={`${label}`} />
          </div>
          {username && <div className={styles.userSubtext}>{username}</div>}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        <ShareIcon />
        <div className={styles.text}>
          {canManageAccess ? "Share this dataset" : "People with access"}
        </div>
        <div className={styles.filler} />
        <CloseOutlined className={styles.closeIcon} onClick={onClose} />
      </div>
      <div className={styles.content}>
        {canManageAccess && (
          <div className={styles.onPremInviteSection}>
            <div className={styles.inviteInputRow}>
              <div className={styles.inviteInput}>
                <SelectList
                  options={options}
                  placeholder="Invite Users"
                  value={selectedUser?.label}
                  onChange={(value) => handleSeletedUser(value)}
                  loading={false}
                  open={selectOpen}
                  showSearchIcon={false}
                  placement="bottomLeft"
                  dropdownClassName={styles.userListDropDown}
                  optionRender={(options) => {
                    if (!options || !options.data) {
                      return (
                        <div className={styles.noDataMessage}>
                          No data available
                        </div>
                      );
                    }
                    return renderSingleUser(options.data);
                  }}
                  onDropdownVisibleChange={(open) => {
                    setSelectOpen(open);
                  }}
                />
              </div>
              <Select
                className={styles.roleSelect}
                value={selectedRole}
                onChange={setSelectedRole}
                options={rolesList}
                suffixIcon={<ChevronIcon direction="down" />}
              />
            </div>
            <VLButton
              className={styles.inviteButton}
              type="primary"
              onClick={handleAddUsers}
              disabled={!selectedUser || isSubmitting}
              loading={isSubmitting}
              icon={<PlusOutlined />}
            />
          </div>
        )}
        {owners.length > 0 && (
          <div className={styles.sharedUserDetail}>
            {owners.map((ownerEntry) => (
              <div key={ownerEntry.user.user_id} className={styles.userCard}>
                <div
                  className={styles.userAvatar}
                  data-color-index={getColorIndex(ownerEntry.user.name)}
                >
                  {getInitials(ownerEntry.user.name)}
                </div>
                <div className={styles.userInfo}>
                  <div className={styles.userName}>
                    <TextWithTooltip
                      className={styles.name}
                      text={`${ownerEntry.user.name}`}
                    />
                  </div>
                  <div className={styles.userSubtext}>
                    {ownerEntry.user.username}
                  </div>
                </div>
                <div className={styles.ownerRoleSelect}>Owner</div>
              </div>
            ))}
          </div>
        )}

        <div className={styles.sharedUserDetail}>
          <div className={styles.sharedUserLabel}>Who Has Access</div>
          {collaborators.map((user) => (
            <div key={user?.user?.user_id} className={styles.userCard}>
              <div
                className={styles.userAvatar}
                data-color-index={getColorIndex(user?.user?.name)}
              >
                {getInitials(user?.user?.name)}
              </div>
              <div className={styles.userInfo}>
                <div className={styles.userName}>
                  <TextWithTooltip
                    className={styles.name}
                    text={`${user?.user?.name}`}
                  />
                </div>
                <div className={styles.userSubtext}>{user?.user?.username}</div>
              </div>
              <Select
                className={styles.roleSelect}
                value={user?.role}
                onChange={(value) =>
                  handleRoleChange(user?.user?.user_id, user?.role, value)
                }
                options={rolesListWithRemove}
                disabled={!canManageAccess || isRevoking || isUpdating}
                suffixIcon={<ChevronIcon direction="down" />}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
